import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Loader, Table, Tabs } from '@itds/elements/index.js';
import { Colors } from '@itds/Theme';

import EditProductDrawer from './products/EditProduct';
import EditProductGroupDrawer from './products/EditProductGroup';

import Api from '@carrier/utils/Api';

const Products = ({ carrier }) => {
  const styles = {
    productMenu: {
      width: 300,
      flexShrink: 0,
      padding: '0px 30px',
      margin: '30px 0px',
      borderRight: '1px solid #e5e5e5'
    },
    menuItem: {
      display: 'block',
      textDecoration: 'none',
      color: Colors.GRAY.hex,
      fontSize: 14,
      padding: '12px 10px',
      margin: '14px 0px'
    },
    menuItemIcon: {
      fontSize: 20,
      cursor: 'pointer',
      marginLeft: 4,
      color: Colors.PRIMARY.hex
    },
    active: {
      fontWeight: 700,
      backgroundColor: `rgba(${Colors.PRIMARY.rgb}, 0.10)`,
      borderRadius: 8
    },
    content: {
      width: '100%',
      padding: '0px 40px'
    },
    topBar: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: 30
    }
  };
  const [loading, setLoading] = useState(true);
  const [product_groups, setProductGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState(null);
  const [product_group, setProductGroup] = useState(null);
  const [filter, setFilter] = useState(null);

  useEffect(() => {
    loadProductGroups();
  }, []);

  const loadProductGroups = () => {
    const params = { carrier_id: carrier.id };

    Api.loadProductGroups(params).then(pg_response => {
      setProductGroups(pg_response.data);

      Api.loadProducts(params).then(p_response => {
        setProducts(p_response.data);
        setLoading(false);
      });
    });
  };

  const handleProductGroupSave = updated_pg => {
    const new_product_groups = product_groups.filter(pg => pg.id !== updated_pg.id);

    new_product_groups.push(updated_pg);

    setProductGroups(new_product_groups);
    setProductGroup(null);
  };

  const handleProductGroupDelete = deleted_pg => {
    const new_product_groups = product_groups.filter(pg => pg.id !== deleted_pg.id);

    setProducts(new_product_groups);
    loadProductGroups();
    setProductGroup(null);
  };

  const handleProductSave = updated_p => {
    const new_products = products.filter(p => p.id !== updated_p.id);

    new_products.push(updated_p);

    setProducts(new_products);
    setProduct(null);
  };

  const handleProductDelete = deleted_p => {
    const new_products = products.filter(p => p.id !== deleted_p.id);

    setProducts(new_products);
    setProduct(null);
  };

  const sorted_product_groups = product_groups.sort((a, b) => {
    return a.group_name.toLowerCase() < b.group_name.toLowerCase() ? -1 : 0;
  });

  return loading ? (
    <Loader />
  ) : (
    <div style={{ display: 'flex' }}>
      <div>
        <Tabs
          tabs={[
            {
              label: 'All',
              isActive: !filter,
              onClick: () => setFilter(null)
            }
          ].concat(
            sorted_product_groups.map(product_group => {
              return {
                label: product_group.group_name,
                isActive: filter === product_group.id,
                onClick: () => setFilter(product_group.id)
              };
            })
          )}
          vertical={true}
          width={250}
        />

        <div style={{ padding: 20 }}>
          <Button onClick={setProductGroup.bind(null, { carrier_id: carrier.id, group_name: '' })}>
            New Product Group
          </Button>
        </div>
      </div>
      <div style={styles.content}>
        <div style={styles.topBar}>
          {filter ? (
            <Button
              onClick={setProductGroup.bind(
                null,
                product_groups.find(pg => pg.id === filter)
              )}
              outline={true}
            >
              Edit Product Group
            </Button>
          ) : null}
          <Button
            onClick={setProduct.bind(null, { carrier_id: carrier.id })}
            style={{ marginLeft: 10 }}
          >
            New Product
          </Button>
        </div>

        <Table
          columns={[
            {
              id: 'product_name',
              label: 'Name',
              is_sortable: true
            },
            {
              id: 'id',
              label: 'ID'
            },
            {
              id: 'product_type',
              label: 'Type',
              is_sortable: true
            },
            {
              id: 'product_type_detail',
              label: 'Detail',
              is_sortable: true
            },
            {
              id: 'death_benefit_types',
              label: 'Death Benefits',
              render: product => {
                // Display '-' for null, undefined, or empty arrays
                if (!product.death_benefit_types || product.death_benefit_types.length === 0) {
                  return '-';
                }
                // Display comma-separated values for IUL products
                if (product.product_type === 'IUL') {
                  return product.death_benefit_types.join(', ');
                }
                return '-';
              }
            }
          ]}
          data={products.filter(p => !filter || p.product_group_id === filter)}
          defaultSortColumn={{ id: 'product_name' }}
          onRowClick={setProduct}
          placeholder='No products in this group'
        />

        <EditProductDrawer
          onClose={setProduct.bind(null, null)}
          onDelete={handleProductDelete}
          onSave={handleProductSave}
          product={product}
          productGroups={product_groups}
          show={!!product}
        />

        <EditProductGroupDrawer
          onClose={setProductGroup.bind(null, null)}
          onDelete={handleProductGroupDelete}
          onSave={handleProductGroupSave}
          productGroup={product_group}
          show={!!product_group}
        />
      </div>
    </div>
  );
};

Products.propTypes = {
  carrier: PropTypes.object
};

export default Products;
